import { injectable } from 'inversify';
import {
  GiveawayResponse,
  GiveawayEligibilityResponse,
  SubscriptionCancellation,
  SubscriptionMigrationResponse,
  SubscriptionData,
  SubscriptionEntitlementType,
  SubscriptionPurchasableData,
  UpsellResponse,
  UpdatePaymentResponse,
} from 'src/utilities/types';
import {
  getHighestPriorityActiveSubscription,
  getIsSubscriptionActive,
  getIsSubscriptionPaused,
} from 'src/modules/Subscriptions/utilities/subscriptions-utility';
import { buildQueryString } from 'src/utilities/query-string-utility';
import {
  SubscriptionList,
  SubscriptionListImpl,
} from '../models/SubscriptionList.model';
import { apiClient } from './NativeApiClient';

export interface SubscriptionService {
  fetchSubscriptionData(): Promise<SubscriptionList>;
  checkGiveawayEligibility(
    options: CheckEligibilityOptions,
  ): Promise<GiveawayEligibilityResponse>;
  acceptGiveaway(options: AcceptGiveawayOptions): Promise<GiveawayResponse>;
  cancelSubscription(subscriptionId: string): Promise<SubscriptionCancellation>;
  migrateSubscription(options: {
    targetSubscriptionId: string;
    paymentId?: string;
  }): Promise<SubscriptionMigrationResponse>;
  acceptUpsell(): Promise<UpsellResponse>;
  updatePayment(options: {
    subscriptionId: string;
    paymentId: string;
    stateCode?: string;
  }): Promise<UpdatePaymentResponse>;
  getManageableSubscription(): Promise<SubscriptionResponse>;
  getActiveSubscription(options?: {
    includeMigrations?: boolean;
  }): Promise<SubscriptionResponse>;
  getPurchasableSubscription(options?: {
    includeMigrations?: boolean;
  }): Promise<SubscriptionResponse>;
  getAllSubscriptions(options?: {
    variationId?: string;
    managedProvider?: string;
    suiteId?: string;
    includeMigrations?: boolean;
  }): Promise<{
    subscriptions: SubscriptionData[];
    availableEntitlementTypes: SubscriptionEntitlementType[];
  }>;
}

export interface SubscriptionResponse {
  subscription: SubscriptionData;
  migrationTargetSubscription?: SubscriptionData;
  availableEntitlementTypes: SubscriptionEntitlementType[];
  inAuthTransactionId?: string; // Only populated for /new subscriptions
}

type CheckEligibilityOptions = {
  giveawayId: string;
  token?: string;
};

type AcceptGiveawayOptions = {
  giveawayId: string;
  partnerId?: string;
};

@injectable()
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export class SubscriptionServiceImpl {
  public async fetchSubscriptionData(): Promise<SubscriptionList> {
    const data = await apiClient.get<{
      subscriptions: SubscriptionData[];
      available_entitlement_types: SubscriptionEntitlementType[];
    }>('/subscription/all?textVersion=v2');
    return new SubscriptionListImpl(data.subscriptions);
  }

  /**
   * Returns all subscriptions to the user
   */
  public async getAllSubscriptions(
    options: {
      variationId?: string;
      managedProvider?: string;
      suiteId?: string;
      includeMigrations?: boolean;
    } = {},
  ): Promise<{
    subscriptions: SubscriptionData[];
    availableEntitlementTypes: SubscriptionEntitlementType[];
  }> {
    try {
      const queryString = buildQueryString({ textVersion: 'v2', ...options });
      const response = await apiClient.get<{
        subscriptions: SubscriptionData[];
        available_entitlement_types: SubscriptionEntitlementType[];
      }>(`/subscription/all${queryString}`);

      const subscriptions = response?.subscriptions || [];
      return {
        subscriptions,
        availableEntitlementTypes: response.available_entitlement_types || [],
      };
    } catch (e) {
      console.warn(e);
      return {
        subscriptions: [],
        availableEntitlementTypes: [],
      };
    }
  }

  /**
   * Returns available subscription to the user
   */
  public async getPurchasableSubscription(
    options: { includeMigrations?: boolean } = {},
  ): Promise<SubscriptionResponse> {
    try {
      const queryString = buildQueryString({ textVersion: 'v2', ...options });
      const response = await apiClient.get<SubscriptionPurchasableData>(
        `/subscription/new${queryString}`,
      );
      const subscription =
        response &&
        response.subscriptions &&
        (response.subscriptions[0] as unknown as SubscriptionData);
      return {
        subscription,
        availableEntitlementTypes: response.available_entitlement_types,
        inAuthTransactionId: response.in_auth_transaction_id,
      };
    } catch (e) {
      console.warn(e);
      return {
        subscription: null,
        availableEntitlementTypes: [],
      };
    }
  }

  /**
   * Retrieves the diner's highest-priority subscription from `/subscription/all`
   * (priority: EXISTING, then CANCELLED), plus optional migration target from the same payload.
   */
  public async getActiveSubscription(
    options: { includeMigrations?: boolean } = {},
  ): Promise<SubscriptionResponse> {
    try {
      const queryString = buildQueryString({ textVersion: 'v2', ...options });
      const response = await apiClient.get<{
        subscriptions: SubscriptionData[];
        available_entitlement_types: SubscriptionEntitlementType[];
      }>(`/subscription/all${queryString}`);

      const subscriptions = response?.subscriptions || [];
      const subscription = getHighestPriorityActiveSubscription(subscriptions);
      const migrationTargetSubscription = subscriptions.find((sub) => {
        return sub.plan_migration === true;
      });

      return {
        subscription,
        availableEntitlementTypes: response.available_entitlement_types || [],
        ...(migrationTargetSubscription && { migrationTargetSubscription }),
      };
    } catch (e) {
      console.warn(e);
      return {
        subscription: null,
        availableEntitlementTypes: [],
      };
    }
  }

  public async getManageableSubscription(): Promise<SubscriptionResponse> {
    try {
      const response = await apiClient.get<{
        subscriptions: SubscriptionData[];
        available_entitlement_types: SubscriptionEntitlementType[];
      }>('/subscription/all?textVersion=v2');
      const subscription = response?.subscriptions?.[0];
      if (
        subscription &&
        (getIsSubscriptionActive(subscription) ||
          getIsSubscriptionPaused(subscription))
      ) {
        return {
          subscription,
          availableEntitlementTypes: [],
        };
      } else {
        throw new Error('No manageable subscription found');
      }
    } catch (e) {
      console.warn(e);
      return {
        subscription: null,
        availableEntitlementTypes: [],
      };
    }
  }

  public async checkGiveawayEligibility(
    options: CheckEligibilityOptions,
  ): Promise<GiveawayEligibilityResponse> {
    try {
      const queryParams: Record<string, any> = {};
      if (options.token) {
        queryParams.token = options.token;
      }
      const queryString = buildQueryString(queryParams);
      const data = await apiClient.get<GiveawayEligibilityResponse>(
        `/subscription/giveaway/eligibility/${options.giveawayId}${queryString}`,
      );
      return data;
    } catch (e) {
      console.warn(e);
      return (e as any).data;
    }
  }

  public async acceptGiveaway(
    options: AcceptGiveawayOptions,
  ): Promise<GiveawayResponse> {
    try {
      const payload: Record<string, any> = {
        giveaway_id: options.giveawayId,
      };
      if (options.partnerId) {
        payload.partner_id = options.partnerId;
      }
      const data = await apiClient.post<GiveawayResponse>(
        '/subscription/giveaway',
        payload,
      );
      return data;
    } catch (e) {
      console.warn(e);
      return (e as any).data;
    }
  }

  public async cancelSubscription(
    subscriptionId: string,
  ): Promise<SubscriptionCancellation> {
    try {
      const response = await apiClient.delete<SubscriptionCancellation>(
        `/subscription/${subscriptionId}`,
      );
      return response;
    } catch (e) {
      console.warn(e);
      return null;
    }
  }

  public async migrateSubscription(options: {
    targetSubscriptionId: string;
    paymentId?: string;
  }): Promise<SubscriptionMigrationResponse> {
    try {
      const payload: Record<string, any> = {
        target_subscription_id: options.targetSubscriptionId,
      };
      if (options.paymentId) {
        payload.payment_id = options.paymentId;
      }
      const response = await apiClient.post<SubscriptionMigrationResponse>(
        '/subscription/diner/plan/migrate',
        payload,
      );
      return response;
    } catch (e) {
      console.warn(e);
      return (e as any).data;
    }
  }

  public async acceptUpsell(): Promise<UpsellResponse> {
    try {
      const response = await apiClient.put<UpsellResponse>(
        '/subscription/upsell',
        {},
      );
      return response;
    } catch (e) {
      console.warn(e);
      throw e;
    }
  }

  public async updatePayment(options: {
    subscriptionId: string;
    paymentId: string;
    stateCode?: string;
  }): Promise<UpdatePaymentResponse> {
    try {
      const payload: Record<string, any> = {
        subscription_id: options.subscriptionId,
        payment_id: options.paymentId,
        state_code: options.stateCode,
      };
      const response = await apiClient.put<UpdatePaymentResponse>(
        '/subscription',
        payload,
      );
      return response;
    } catch (e) {
      console.warn(e);
      throw e;
    }
  }
}
